import React, { forwardRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import downArrowIcon from 'assets/icons/arrow-down.svg';
import searchIcon from 'assets/icons/search.svg';
import LoaderSpinner from 'components/elements/LoaderSpinner';

import { StyledInput, StyledLabel, IconImg, LoaderWrapper } from '../styles';
import Menu from './Menu';

function getOrderedOptions(options, searchedValue) {
  const { resultsStartWithValue, resultsHasValue } = options.reduce(
    (allOptions, option) => {
      const label = option.label?.toLowerCase();

      if (label?.startsWith(searchedValue?.toLowerCase())) allOptions.resultsStartWithValue.push(option);
      else if (label?.includes(searchedValue?.toLowerCase())) allOptions.resultsHasValue.push(option);

      return allOptions;
    },
    {
      resultsStartWithValue: [],
      resultsHasValue: [],
    },
  );
  return [...resultsStartWithValue, ...resultsHasValue];
}

const Select = forwardRef(
  (
    {
      name,
      value = '',
      defaultValue,
      placeholder,
      onSelect,
      onFocus,
      onChange,
      options,
      showIcons = true,
      showLoader = false,
      showSearchedResults = false,
      ...props
    },
    ref,
  ) => {
    const [searchLabel, setSearchLabel] = useState('');
    const [selectedValue, setSelectedValue] = useState(value);
    const [showDropdownMenu, setShowDropdownMenu] = useState(false);

    const filteredOptions =
      !showSearchedResults && searchLabel.length > 0 ? getOrderedOptions(options, searchLabel) : options;

    function handleSelect(option) {
      onSelect(option);
      setShowDropdownMenu(false);
      setSearchLabel(option.label);
    }

    function onChangeHandler(e) {
      const { value } = e.target;
      setSearchLabel(value);
      setShowDropdownMenu(true);
      onChange?.(value);
    }

    function focusHandler(e) {
      e.preventDefault();
      setShowDropdownMenu(true);
      if (searchLabel?.length > 0) return;
      onFocus?.();
    }

    function onBlur(e) {
      e.preventDefault();
      setTimeout(() => setShowDropdownMenu(false), 200);
    }

    useEffect(() => {
      const defaultOption = options.find(item => item.value === defaultValue);

      if (defaultOption) {
        setSearchLabel(defaultOption.label);
        setSelectedValue(defaultValue);
      }
    }, []);

    return (
      <StyledLabel>
        <StyledInput
          defaultValue={searchLabel}
          value={searchLabel}
          onChange={onChangeHandler}
          onFocus={focusHandler}
          onBlur={onBlur}
          placeholder={placeholder}
          {...props}
        />
        <input name={name} ref={ref} type="input" hidden value={selectedValue} />
        {showIcons ? (
          <>
            <IconImg src={searchIcon} alt="Search icon" />
            <IconImg src={downArrowIcon} alt="Down arrow icon" />
          </>
        ) : null}

        {showLoader ? (
          <LoaderWrapper>
            <LoaderSpinner color="#266ff2" />
          </LoaderWrapper>
        ) : null}

        {(showDropdownMenu || showSearchedResults) && !showLoader ? (
          <Menu onSelect={handleSelect} options={filteredOptions} />
        ) : null}
      </StyledLabel>
    );
  },
);

export default Select;

Select.propTypes = {
  name: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  placeholder: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  showLoader: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ).isRequired,
};
